import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Loading, Screen, TextField } from '../components/ui';
import { api, ApiError } from '../lib/api';
import { GuestHead } from './Bulawa';
import './guest.css';

/**
 * The join screen (§6): name + phone, pre-filled if we know them, then a device
 * check, then a knock at the door.
 *
 * The phone number is the identity (§3) — it is what links this person to their
 * record, and links a new number into the family of whoever invited them (§4).
 */

type State =
  | { s: 'loading' }
  | { s: 'ready'; name: string; phone: string; from: string | null }
  | { s: 'gone'; kind: string }
  | { s: 'error'; message: string };

export default function Join() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [state, setState] = useState<State>({ s: 'loading' });
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [devices, setDevices] = useState<'checking' | 'ok' | 'blocked'>('checking');

  const load = useCallback(async () => {
    try {
      const res = await api<{
        state: string;
        expected_name?: string | null;
        expected_phone?: string | null;
        from?: string | null;
      }>(`/api/g/join/${token}`);

      if (res.state === 'ready') {
        setName((n) => n || res.expected_name || '');
        setPhone((p) => p || res.expected_phone || '');
        setState({ s: 'ready', name: res.expected_name ?? '', phone: res.expected_phone ?? '', from: res.from ?? null });
      } else {
        setState({ s: 'gone', kind: res.state });
      }
    } catch (err) {
      setState({ s: 'error', message: err instanceof ApiError ? err.message : 'Could not open this.' });
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * The device check (§6). Asking here — on a calm screen with an explanation —
   * is far kinder than the browser popping the permission bar mid-greeting.
   */
  useEffect(() => {
    let stream: MediaStream | null = null;
    void (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        setDevices('ok');
      } catch {
        setDevices('blocked');
      } finally {
        // Release immediately; Daily opens its own stream when the call starts.
        stream?.getTracks().forEach((t) => t.stop());
      }
    })();
    return () => stream?.getTracks().forEach((t) => t.stop());
  }, []);

  /**
   * Waiting to be let in happens inside Daily's knocking room on the next
   * screen, not here — so this screen has no waiting state of its own. While the
   * call has not started yet, though, keep looking, so the guest never has to
   * reload anything (§0.5).
   */
  useEffect(() => {
    if (state.s !== 'gone' || state.kind !== 'not_started') return;
    const t = setInterval(() => void load(), 4000);
    return () => clearInterval(t);
  }, [state, load]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setFormError(null);
    try {
      const res = await api<{ state: string }>(`/api/g/join/${token}`, {
        method: 'POST',
        body: { name, phone },
      });
      if (res.state === 'joined') {
        navigate(`/call/${token}?name=${encodeURIComponent(name)}`);
        return;
      }
      setState({ s: 'gone', kind: res.state });
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Could not join. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  if (state.s === 'loading') {
    return (
      <Screen>
        <GuestHead word="Mulaqaat" />
        <Card>
          <Loading label="One moment…" />
        </Card>
      </Screen>
    );
  }

  if (state.s === 'error') {
    return (
      <Screen>
        <GuestHead word="Mulaqaat" />
        <Card>
          <p className="g__gone">{state.message}</p>
          <Button onClick={() => void load()}>Try again</Button>
        </Card>
      </Screen>
    );
  }

  if (state.s === 'gone') {
    const line =
      state.kind === 'not_started'
        ? 'The shop has not opened the Mulaqaat yet. Keep this page open — it will be ready in a moment.'
        : state.kind === 'declined'
          ? 'You already told the shop you cannot talk now.'
          : state.kind === 'expired'
            ? 'This link has passed. Please phone the shop.'
            : 'This link is not one we recognise. Please check the message again.';

    return (
      <Screen>
        <GuestHead word="Mulaqaat" />
        <Card>
          <p className="g__gone">{line}</p>
          {state.kind === 'not_started' ? (
            <Button variant="secondary" onClick={() => void load()}>
              Check again
            </Button>
          ) : null}
        </Card>
      </Screen>
    );
  }

  return (
    <Screen>
      <GuestHead word="Mulaqaat" />
      <Card>
        {state.from ? <p className="g__from">{state.from} is waiting for you.</p> : null}

        <form onSubmit={submit} noValidate>
          <TextField
            label="Your name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={80}
            autoComplete="name"
          />
          <TextField
            label="Your phone number"
            type="tel"
            inputMode="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            hint="So the shop knows it is you."
            autoComplete="tel"
          />

          <div className={`g__devices g__devices--${devices}`}>
            {devices === 'checking'
              ? 'Checking your camera and microphone…'
              : devices === 'ok'
                ? '✓ Camera and microphone are ready'
                : 'Your browser has not allowed the camera or microphone. You can still join to listen, or allow them and reload.'}
          </div>

          {formError ? (
            <p className="field__error" role="alert" style={{ marginBottom: 14 }}>
              {formError}
            </p>
          ) : null}

          <Button type="submit" full busy={busy} disabled={!name.trim() || !phone.trim()}>
            {busy ? 'Joining…' : 'Join the Mulaqaat'}
          </Button>
        </form>
      </Card>
    </Screen>
  );
}
